import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, ShieldCheck, ArrowRight, ImageIcon, Lock, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Media consent onboarding — blocks all users on next login until they answer.
 *
 * Two audiences share the screen:
 *   • Players / students → single yes/no question (do you consent to being filmed?).
 *   • Coaches (and admin/coach dual-role) → the same subject question PLUS a
 *     separate data-controller acknowledgement they must tick to continue.
 *
 * The backend versions the consent (MEDIA_CONSENT_VERSION); bumping that on
 * server-side re-prompts everyone here. We refresh /auth/me after each POST so
 * ProtectedRoute lets the user through immediately without a manual reload.
 */
export default function MediaConsent() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [subject, setSubject] = useState(""); // "granted" | "declined"
  const [ack, setAck] = useState(false);       // coach-only
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(null);
  // Iter67 hardening: derive the three consent flags via useMemo so the effect
  // deps below are stable primitives regardless of how many times the parent
  // AuthProvider re-renders and hands us a fresh user object.
  const isCoach = useMemo(
    () => user?.role === "coach" || user?.role === "admin",
    [user?.role],
  );
  const needsSubject = useMemo(() => Boolean(user?.media_consent_needed), [user?.media_consent_needed]);
  const needsAck = useMemo(
    () => isCoach && Boolean(user?.coach_media_ack_needed),
    [isCoach, user?.coach_media_ack_needed],
  );
  // Ref guard: the "nothing to prompt for" bounce must fire AT MOST once per
  // mount. Without this, an unstable user reference from AuthProvider could
  // re-trigger the effect after nav() completes and burn the CPU / API budget.
  const bouncedRef = useRef(false);

  // Live-loaded consent copy — admin can edit via /admin/media-consent.
  useEffect(() => {
    api.get("/media-consent-config")
      .then(({ data }) => setCfg(data))
      .catch(() => setCfg(null));
  }, []);

  // Nothing to prompt for — user landed here manually or via a stale redirect
  // after their record was already fully answered. Bounce back to the dashboard
  // so we never render an empty form shell.
  useEffect(() => {
    if (bouncedRef.current) return;
    if (user && !needsSubject && !needsAck) {
      bouncedRef.current = true;
      nav("/dashboard", { replace: true });
    }
  }, [user, needsSubject, needsAck, nav]);

  const submit = async () => {
    if (needsSubject && !subject) {
      toast.error("Please answer the consent question to continue.");
      return;
    }
    if (needsAck && !ack) {
      toast.error("Please tick the coach acknowledgement to continue.");
      return;
    }
    setSaving(true);
    try {
      if (needsSubject) {
        await api.post("/me/media-consent", { status: subject });
      }
      if (needsAck) {
        await api.post("/me/coach-media-ack", { acknowledged: true });
      }
      // Force /auth/me to re-read so the ProtectedRoute gate clears.
      if (refresh) await refresh();
      toast.success("Thanks — noted.");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-6 py-10 noise-bg" data-testid="media-consent-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-[#CCFF00] flex items-center justify-center">
            <ShieldCheck strokeWidth={2.5} className="w-5 h-5 text-black" />
          </div>
          <div className="label-mono">MEDIA CONSENT · REQUIRED</div>
        </div>

        <h1 className="h-display text-4xl sm:text-5xl mb-3 tracking-tighter">
          One quick question before you carry on.
        </h1>
        <p className="text-sm text-[#A3A3A3] mb-8 leading-relaxed">
          To keep everyone protected we need every player and coach on Rally Lab to
          record a media-consent preference. Takes 20 seconds — you can change your
          answer any time from your profile.
        </p>

        {/* Subject consent — asked of both players and coaches */}
        {needsSubject && (
          <section className="card-surface p-5 mb-5" data-testid="media-consent-subject">
            <div className="flex items-center gap-2 mb-2">
              <Camera className="w-4 h-4 text-[#CCFF00]" />
              <div className="label-mono">YOUR CONSENT</div>
            </div>
            <h2 className="h-display text-2xl tracking-tight mb-2">
              {cfg?.player_question || "Can coaches take photos or short videos of you during sessions?"}
            </h2>
            <p className="text-sm text-[#A3A3A3] mb-4 leading-relaxed">
              {cfg?.player_description || (<>Clips are only used for on-court analysis &amp; debriefing between you and your coach
              — never posted publicly, never shared with anyone outside the coaching team.
              If you decline, coaches will be flagged before every session so they know not to record.</>)}
            </p>

            <div className="grid sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSubject("granted")}
                data-testid="media-consent-granted"
                className="text-left p-4 border transition-all"
                style={subject === "granted"
                  ? { borderColor: "#CCFF00", background: "rgba(204,255,0,0.06)" }
                  : { borderColor: "#262626" }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <ImageIcon className="w-4 h-4" style={{ color: subject === "granted" ? "#CCFF00" : "#A3A3A3" }} />
                  <div className="label-mono" style={{ color: subject === "granted" ? "#CCFF00" : "#A3A3A3" }}>YES, I CONSENT</div>
                </div>
                <div className="text-xs text-[#A3A3A3] leading-relaxed">
                  Coaches can record video / take photos of me for coaching analysis.
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSubject("declined")}
                data-testid="media-consent-declined"
                className="text-left p-4 border transition-all"
                style={subject === "declined"
                  ? { borderColor: "#FF4500", background: "rgba(255,69,0,0.06)" }
                  : { borderColor: "#262626" }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Lock className="w-4 h-4" style={{ color: subject === "declined" ? "#FF4500" : "#A3A3A3" }} />
                  <div className="label-mono" style={{ color: subject === "declined" ? "#FF4500" : "#A3A3A3" }}>NO, PLEASE DON&apos;T</div>
                </div>
                <div className="text-xs text-[#A3A3A3] leading-relaxed">
                  No photos, no video. Coaches will be reminded on each of your bookings.
                </div>
              </button>
            </div>
          </section>
        )}

        {/* Data-controller acknowledgement — coaches / admins only */}
        {needsAck && (
          <section className="card-surface p-5 mb-5" data-testid="media-consent-ack">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-[#CCFF00]" />
              <div className="label-mono">COACH ACKNOWLEDGEMENT</div>
            </div>
            <h2 className="h-display text-2xl tracking-tight mb-2">
              Data controller responsibilities.
            </h2>
            <p className="text-sm text-[#A3A3A3] mb-4 leading-relaxed">
              Because you may capture footage of others during sessions, we need you to confirm
              you understand your responsibilities as a data controller under UK GDPR.
            </p>

            <label
              className="flex items-start gap-3 p-4 border cursor-pointer transition-all"
              style={ack ? { borderColor: "#CCFF00", background: "rgba(204,255,0,0.06)" } : { borderColor: "#262626" }}
              data-testid="media-consent-ack-toggle"
            >
              <input
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#CCFF00] shrink-0"
                data-testid="media-consent-ack-checkbox"
              />
              <span className="text-xs text-[#E5E5E5] leading-relaxed">
                {cfg?.coach_ack_text || (<>I confirm I will only take photos or video during sessions where the player has
                granted media consent. I will store recordings securely, use them solely for
                coaching analysis &amp; debriefing, and delete any recording immediately if the
                player requests it or withdraws their consent.</>)}
              </span>
            </label>
          </section>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
          <p className="text-[10px] uppercase tracking-wider text-[#525252]">
            You can change your answer any time from your profile page.
          </p>
          <button
            onClick={submit}
            disabled={saving || (needsSubject && !subject) || (needsAck && !ack)}
            className="btn-volt px-6 py-3 text-xs inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="media-consent-submit"
          >
            {saving ? "Saving…" : <>Continue <ArrowRight className="w-3.5 h-3.5" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
